import { Camera } from "../../d3/core/Camera";
import { DepthPass } from "../../d3/depthMap/DepthPass";
import { CameraCullInfo } from "../../d3/shadowMap/ShadowSliceData";
import { Color } from "../../maths/Color";
import { Vector4 } from "../../maths/Vector4";
import { RenderClearFlag } from "../../RenderEngine/RenderEnum/RenderClearFlag";
import { DepthTextureMode } from "../../resource/RenderTexture";
import { RenderCullUtil } from "./RenderCullUtil";
import { RenderPassUtil } from "./RenderPassUtil";
import { RenderListQueue } from "./RenderListQueue";
import { Viewport } from "../../maths/Viewport";
import { Texture2D } from "../../resource/Texture2D";
import { LayaGL } from "../../layagl/LayaGL";
import { StatElement } from "../../layagl/StatisticsContext";
import { Browser } from "../../utils/Browser";
export class ForwardAddClusterRP {
    setViewPort(value) {
        value.cloneTo(this._viewPort);
    }
    setScissor(value) {
        value.cloneTo(this._scissor);
    }
    constructor() {
        this._opaqueList = new RenderListQueue(false);
        this._transparent = new RenderListQueue(true);
        this.cameraCullInfo = new CameraCullInfo();
        this._zBufferParams = new Vector4();
        this._scissor = new Vector4();
        this._viewPort = new Viewport();
        this._defaultNormalDepthColor = new Color(0.5, 0.5, 1, 0);
        this.clearColor = new Color();
        this.depthPipelineMode = "ShadowCaster";
        this.depthNormalPipelineMode = "DepthNormal";
    }
    setCameraCullInfo(camera) {
        this.cameraCullInfo.position = camera._transform.position;
        this.cameraCullInfo.cullingMask = camera.cullingMask;
        this.cameraCullInfo.staticMask = camera.staticMask;
        this.cameraCullInfo.boundFrustum = camera.boundFrustum;
        this.cameraCullInfo.useOcclusionCulling = camera.useOcclusionCulling;
    }
    setBeforeForwardCmds(value) {
        if (value && value.length > 0) {
            this.beforeForwardCmds = value;
            value.forEach(element => element._apply(false));
        }
    }
    setBeforeSkyboxCmds(value) {
        if (value && value.length > 0) {
            this.beforeSkyboxCmds = value;
            value.forEach(element => element._apply(false));
        }
    }
    setBeforeTransparentCmds(value) {
        if (value && value.length > 0) {
            this.beforeTransparentCmds = value;
            value.forEach(element => element._apply(false));
        }
    }
    render(context, list, count) {
        context.cameraUpdateMask++;
        this._clearRenderList();
        var time = Browser.now();
        RenderCullUtil.cullByCameraCullInfo(this.cameraCullInfo, list, count, this._opaqueList, this._transparent, context);
        LayaGL.statAgent.recordTimeData(StatElement.T_CullMain, Browser.now() - time);
        time = Browser.now();
        if ((this.depthTextureMode & DepthTextureMode.Depth) != 0)
            this._renderDepthPass(context);
        if ((this.depthTextureMode & DepthTextureMode.DepthNormals) != 0)
            this._renderDepthNormalPass(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_DepthPass, Browser.now() - time);
        this._cacheViewPortAndScissor();
        time = Browser.now();
        this._mainPass(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass, Browser.now() - time);
        this._opaqueList._batch.recoverData();
    }
    _clearRenderList() {
        this._opaqueList.clear();
        this._transparent.clear();
    }
    _cacheViewPortAndScissor() {
        this._viewPort.cloneTo(RenderPassUtil.contextViewPortCache);
        this._scissor.cloneTo(RenderPassUtil.contextScissorCache);
    }
    _renderDepthPass(context) {
        context.pipelineMode = this.depthPipelineMode;
        const viewport = this._viewPort;
        const shadervalue = context.sceneData;
        shadervalue.addDefine(DepthPass.DEPTHPASS);
        shadervalue.setVector(DepthPass.DEFINE_SHADOW_BIAS, Vector4.ZERO);
        Viewport.TEMP.set(viewport.x, viewport.y, viewport.width, viewport.height);
        Vector4.TEMP.setValue(viewport.x, viewport.y, viewport.width, viewport.height);
        context.setViewPort(Viewport.TEMP);
        context.setScissor(Vector4.TEMP);
        context.setRenderTarget(this.depthTarget, RenderClearFlag.Depth);
        context.setClearData(RenderClearFlag.Depth, Color.BLACK, 1, 0);
        this._opaqueList.renderQueue(context);
        LayaGL.statAgent.recordCTData(StatElement.CT_DepthCastDrawCall, this._opaqueList.elements.length);
        const far = this.camera.farPlane;
        const near = this.camera.nearPlane;
        this._zBufferParams.setValue(1.0 - far / near, far / near, (near - far) / (near * far), 1 / near);
        context.cameraData.setVector(DepthPass.DEFINE_SHADOW_BIAS, DepthPass.SHADOW_BIAS);
        context.cameraData.setVector(DepthPass.DEPTHZBUFFERPARAMS, this._zBufferParams);
        Camera.depthPass._setupDepthModeShaderValue(DepthTextureMode.Depth, this.camera);
        shadervalue.removeDefine(DepthPass.DEPTHPASS);
    }
    _renderDepthNormalPass(context) {
        context.pipelineMode = this.depthNormalPipelineMode;
        this.camera._shaderValues.setTexture(DepthPass.DEPTHNORMALSTEXTURE, Texture2D.blackTexture);
        const viewport = this._viewPort;
        Viewport.TEMP.set(viewport.x, viewport.y, viewport.width, viewport.height);
        Vector4.TEMP.setValue(viewport.x, viewport.y, viewport.width, viewport.height);
        context.setViewPort(Viewport.TEMP);
        context.setScissor(Vector4.TEMP);
        context.setClearData(RenderClearFlag.Color | RenderClearFlag.Depth, this._defaultNormalDepthColor, 1, 0);
        context.setRenderTarget(this.depthNormalTarget, RenderClearFlag.Color | RenderClearFlag.Depth);
        this._opaqueList.renderQueue(context);
        LayaGL.statAgent.recordCTData(StatElement.CT_DepthCastDrawCall, this._opaqueList.elements.length);
        Camera.depthPass._setupDepthModeShaderValue(DepthTextureMode.DepthNormals, this.camera);
    }
    _mainPass(context) {
        context.pipelineMode = this.pipelineMode;
        RenderPassUtil.renderCmd(this.beforeForwardCmds, context);
        RenderPassUtil.recoverRenderContext3D(context, this.destTarget);
        context.setClearData(this.clearFlag, this.clearColor, 1, 0);
        var time = Browser.now();
        this._opaqueList.renderQueue(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass_Opaque, Browser.now() - time);
        RenderPassUtil.renderCmd(this.beforeSkyboxCmds, context);
        if (this.skyRenderNode) {
            const skyRenderElement = this.skyRenderNode.renderelements[0];
            if (skyRenderElement.subShader)
                context.drawRenderElementOne(skyRenderElement);
        }
        if (this.enableOpaque)
            this._opaqueTexturePass();
        RenderPassUtil.renderCmd(this.beforeTransparentCmds, context);
        RenderPassUtil.recoverRenderContext3D(context, this.destTarget);
        time = Browser.now();
        this._transparent.renderQueue(context);
        LayaGL.statAgent.recordTimeData(StatElement.T_3DMainPass_Trans, Browser.now() - time);
    }
    _opaqueTexturePass() {
    }
}
